import React, { useState } from 'react';
import { Form, Divider, message, Checkbox, Input } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import InviteMembersSelect from '@components/InviteMembersSelect/InviteMembersSelect';
import { nonNull } from '@helpers/non-null';
import { apiClient } from '@api/client';
import { useCurrentWorkspace } from '@hooks/workspace';
import type { FormValues } from '@components/Modals/ShareAssetModal/ShareAssetModal';
import { CopyIcon, LinkIcon, SuccessIcon } from '@icons';

interface ISharingProps {
  link: string | null;
  previewLink: string | null;
  campaignId: string | null;
  form: FormInstance<FormValues>;
  isEmbeddedWebView?: boolean;
}

function SharingInviteSection({
  campaignId,
  form
}: {
  campaignId: string | null;
  form: FormInstance<FormValues>;
}) {
  const [workspace] = useCurrentWorkspace();
  const isShowMessageWatch = Form.useWatch('showMessage', form);

  return (
    <>
      <Divider className="modal_container_divider" />
      <Form.Item
        className="share_asset_modal_users-item"
        label="Share with people"
        name="userList"
        style={{ marginBottom: 16 }}
      >
        <InviteMembersSelect
          key={campaignId}
          queryEnabled={!!campaignId}
          placeholder="Enter name or email"
          membersQueryKey={
            workspace.permissions.listMembers
              ? ['workspace', 'members', 'list', workspace.id]
              : ['campaign', 'members', 'list', campaignId]
          }
          membersQueryFn={async ({ pageParam, searchQuery, signal }) => {
            if (workspace.permissions.listMembers) {
              const { data } =
                await apiClient.workspace.workspaceControllerListMembers(
                  {
                    workspaceId: workspace.id,
                    searchQuery,
                    after: pageParam
                  },
                  { signal }
                );
              return data;
            }
            const { data } =
              await apiClient.campaign.campaignControllerListMembers(
                {
                  campaignId: nonNull(campaignId),
                  searchQuery,
                  after: pageParam
                },
                { signal }
              );
            return data;
          }}
          groupsQueryKey={
            workspace.permissions.listMembers
              ? ['workspace', 'groups', 'list', workspace.id]
              : ['campaign', 'groups', 'list', campaignId]
          }
          groupsQueryFn={async ({ pageParam, searchQuery, signal }) => {
            if (workspace.permissions.listMembers) {
              const { data } =
                await apiClient.workspace.workspaceControllerListMemberGroups(
                  {
                    workspaceId: workspace.id,
                    after: pageParam,
                    searchQuery
                  },
                  { signal }
                );
              return data;
            }
            const { data } =
              await apiClient.campaign.campaignControllerListMemberGroups(
                {
                  campaignId: nonNull(campaignId),
                  after: pageParam,
                  searchQuery
                },
                { signal }
              );
            return data;
          }}
        />
      </Form.Item>
      {isShowMessageWatch && (
        <Form.Item
          className="share_asset_modal_users-item"
          name="customMessage"
          rules={[{ required: true, message: 'Message required.' }]}
          style={{ marginBottom: 16 }}
        >
          <Input.TextArea placeholder="Add custom message" />
        </Form.Item>
      )}
      <Form.Item
        className="share_asset_modal_checkbox"
        valuePropName="checked"
        name="showMessage"
      >
        <Checkbox className="b-checkbox-blue">Add a message</Checkbox>
      </Form.Item>
    </>
  );
}

function Sharing({
  link,
  previewLink,
  campaignId,
  form,
  isEmbeddedWebView = false
}: ISharingProps) {
  const [isLinkCopied, setIsLinkCopied] = useState<boolean>(false);

  const hasLink = !!link;

  const onCopyLink = () => {
    if (!hasLink) return;
    navigator.clipboard.writeText(link);
    setIsLinkCopied(true);
    message.success({
      content: 'Copied link to clipboard',
      className: 'message-dark-modal',
      icon: <SuccessIcon className="anticon" />
    });
  };

  return (
    <div className="share_asset_modal_form">
      <Form.Item
        className="share_asset_modal_users-item"
        label="Name"
        name="name"
        rules={[
          { required: true, message: 'Name is required' },
          {
            max: 225,
            message: 'Name cannot be longer than 225 characters'
          }
        ]}
      >
        <Input placeholder="Name" />
      </Form.Item>
      {hasLink && (
        <>
          <p className="modal_container__copy">Link URL</p>
          <div className="modal_container__link_block" onClick={onCopyLink}>
            <div className="link_row">
              <LinkIcon />
              <span className="link">{link}</span>
            </div>
            <CopyIcon className={isLinkCopied ? 'isLinkCopied' : ''} />
          </div>
          {previewLink && (
            <a
              target="_blank"
              href={previewLink}
              className="share_asset_modal_preview-link"
              rel="noreferrer"
            >
              Preview link in new tab
            </a>
          )}
        </>
      )}
      {!isEmbeddedWebView && (
        <SharingInviteSection campaignId={campaignId} form={form} />
      )}
    </div>
  );
}

export default Sharing;
